import { useMutation, useQueryClient } from '@tanstack/react-query';

import { rsvpWriteMutationFn } from '../../api/write/rsvp';
import { OFFLINE_MUTATION_DEFAULTS } from '../../lib/mutations/offlineMutationOptions';
import { mutationKeys } from '../../lib/mutations/mutationKeys';
import { assertQueueHasCapacity } from '../../lib/mutations/queueBounds';
import { invalidateAfterRsvpChange } from '../../lib/query/invalidateMutations';
import { warmEventMap } from '../../lib/festivalOffline';
import {
  computeRsvpGoingDelta,
  patchGoingCountInEventList,
  patchGoingCountInInfiniteFeed,
} from '../../lib/query/cachePatches';
import { queryKeys } from '../../lib/query/queryKeys';
import { isEventActionClosed } from '../../utils/eventCutoff';
import {
  rsvpActionFromChoice,
  handleRsvpWriteResult,
} from '../../lib/mutations/rsvpResultHandlers';

export function useRsvpWrite(userId) {
  const queryClient = useQueryClient();
  const mutationKey = userId ? mutationKeys.rsvpWrite(userId) : null;

  return useMutation({
    mutationKey,
    scope: mutationKey ? { id: `rsvp-${userId}` } : undefined,
    ...OFFLINE_MUTATION_DEFAULTS,
    mutationFn: rsvpWriteMutationFn,
    onMutate: async (variables) => {
      assertQueueHasCapacity(queryClient);

      const { action, eventId, status, previousStatus } = variables;
      if (!userId || !eventId) return {};

      const patchMine = (nextStatus) => {
        queryClient.setQueryData(queryKeys.rsvps.mine(userId), (prev = []) => {
          const filtered = prev.filter((r) => r.event_id !== eventId);
          return nextStatus ? [...filtered, { event_id: eventId, status: nextStatus }] : filtered;
        });
      };

      if (action === 'delete') {
        patchMine(null);
        const delta = computeRsvpGoingDelta(previousStatus, null);
        if (delta) {
          patchGoingCountInInfiniteFeed(queryClient, eventId, delta);
          patchGoingCountInEventList(queryClient, eventId, delta);
        }
        return { previousStatus };
      }

      if (action === 'going_skip' || (action === 'upsert' && status === 'going')) {
        patchMine('going');
        const delta = computeRsvpGoingDelta(previousStatus, 'going');
        if (delta) {
          patchGoingCountInInfiniteFeed(queryClient, eventId, delta);
          patchGoingCountInEventList(queryClient, eventId, delta);
        }
        return { previousStatus };
      }

      return {};
    },
    onError: (_err, variables, context) => {
      const { eventId, previousStatus } = variables;
      if (!userId || !eventId) return;

      queryClient.setQueryData(queryKeys.rsvps.mine(userId), (prev = []) => {
        const filtered = prev.filter((r) => r.event_id !== eventId);
        return previousStatus
          ? [...filtered, { event_id: eventId, status: previousStatus }]
          : filtered;
      });

      const delta = computeRsvpGoingDelta(
        variables.action === 'delete' ? 'going' : previousStatus,
        previousStatus,
      );
      if (context?.previousStatus != null && delta) {
        patchGoingCountInInfiniteFeed(queryClient, eventId, -delta);
        patchGoingCountInEventList(queryClient, eventId, -delta);
      }
    },
    onSuccess: (_data, variables) => {
      // Going: start the event's map now, so Maps opens on a map already on the phone (2026-10-13 RSVP-to-map delay).
      const going =
        variables?.action === 'going_skip' ||
        (variables?.action === 'upsert' && variables?.status === 'going');
      if (going) warmEventMap(queryClient, variables.eventId);
    },
    onSettled: (_data, _err, variables) => {
      if (!userId) return;
      invalidateAfterRsvpChange(queryClient, { userId, eventId: variables?.eventId });
    },
  });
}

export function isRsvpMutationQueuedError(error) {
  return error?.code === 'OFFLINE_QUEUE_FULL';
}

export function isMutationPausedOrPending(mutationResult) {
  return Boolean(mutationResult?.isPaused || mutationResult?.isPending);
}

/**
 * Run an RSVP write through the offline queue with cutoff validation.
 */
export async function runRsvpStatusWrite({
  rsvpWrite,
  userId,
  eventId,
  status,
  goingChoice = 'queue',
  previousStatus,
  getEventForCutoff,
  showToast,
  navigate,
  setRejoinModal,
  getRsvpToastMessage,
  skipGroupIds = [],
  groupName = null,
}) {
  const action = rsvpActionFromChoice(status, goingChoice);

  if (action === 'going_skip' || action === 'upsert') {
    const eventForCutoff = await getEventForCutoff?.(eventId);
    if (isEventActionClosed(eventForCutoff)) {
      throw new Error('Joining and group actions are closed for this event.');
    }
  }

  const result = await rsvpWrite.mutateAsync({
    action,
    eventId,
    userId,
    status,
    previousStatus,
    skipGroupIds,
    ...(groupName ? { groupName } : {}),
  });

  return handleRsvpWriteResult(result, {
    showToast,
    navigate,
    setRejoinModal,
    getRsvpToastMessage,
  });
}

export async function runDeleteRsvpWrite({ rsvpWrite, userId, eventId, previousStatus }) {
  return rsvpWrite.mutateAsync({
    action: 'delete',
    eventId,
    userId,
    previousStatus,
  });
}
